import React from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import PageLayout from "../layout/PageLayout";
import PageTitleSection from "../common/PageTitleSection";
import { ServiceContent, ServiceLabels } from "../../types/newReportTypes";
import calendarIcon from "../../assets/calendarIcon.png";
import checkIconBlue from "../../assets/check-single-blue.png";
import checkIconGreen from "../../assets/check-single-green.png";
import ServiceRationaleSection from "../../../platform/components/common/ServiceRationaleSection";
import ServiceAttributesSection from "../../../platform/components/common/ServiceAttributesSection";
import ServiceBenefitsSection from "../../../platform/components/common/ServiceBenefitsSection";
import ServiceActiveCompoundsSection from "../../../platform/components/common/ServiceActiveCompoundsSection";
import { RootStateServiceLogicBuilder } from "../../../platform/store/service-logic-builder-slice";
import { parse } from "date-fns";
import { formatDisplayDate } from "../../../../common/utils/date";

type Props = {
  companyName: string;
  sectionName: string;
  page: ServiceContent;
  pageIndex: number;
  labels: ServiceLabels;
  unitType: number;
};

const ServicePage = ({
  companyName,
  sectionName,
  page,
  pageIndex,
  labels,
  unitType,
}: Props) => {
  const theme: Theme = useTheme();

  // Access Redux state for category options (for field label formatting)
  const categoryOptions = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.categoryOptions
  );

  const rules = page.rational?.list?.[0];

  // Determine active compound title based on service type code
  // 1: Longevity treatments, 2: IV Therapy, 3: Supplements, 4: Pharmaceuticals,
  // 5: Pharmaceuticals IV, 6: Lab work, 7: Medical procedures
  const getActiveCompoundTitle = () => {
    if (page.serviceTypeCode === 3) {
      // Supplements
      return labels.activeSubstancesFormula;
    } else if (page.serviceTypeCode === 4 || page.serviceTypeCode === 5) {
      // Pharmaceuticals or Pharmaceuticals IV
      return labels.activeSubstances;
    } else {
      return labels.activeCompounds;
    }
  };

  return (
    <PageLayout
      companyName={companyName}
      sectionName={sectionName}
      pageIndex={pageIndex}
    >
      <Box sx={styles.pageContent}>
        {/* Service Header with Name and Prescription Period */}
        <Box sx={styles.serviceHeader}>
          <PageTitleSection
            title={page.serviceName}
            subtitle={page.serviceType}
          />
          <Box sx={styles.prescriptionPeriod}>
            <Box component="p" sx={styles.periodLabel(theme)}>
              {labels.prescriptionPeriod}
            </Box>
            <Box sx={styles.periodValue(theme)}>
              <Box component="p" sx={styles.periodText(theme)}>
                {page.prescriptionPeriod.endDate
                  ? `${formatDisplayDate(
                      parse(
                        page.prescriptionPeriod.startDate,
                        "dd-MM-yyyy",
                        new Date()
                      )
                    )} - ${formatDisplayDate(
                      parse(
                        page.prescriptionPeriod.endDate,
                        "dd-MM-yyyy",
                        new Date()
                      )
                    )}`
                  : formatDisplayDate(
                      parse(
                        page.prescriptionPeriod.startDate,
                        "dd-MM-yyyy",
                        new Date()
                      )
                    )}
              </Box>
              <Box
                component="img"
                src={calendarIcon}
                alt="calendar"
                sx={styles.calendarIcon}
              />
            </Box>
          </Box>
        </Box>

        {/* Service Image */}
        {page.imageURL && (
          <Box sx={styles.imageContainer}>
            <Box
              component="img"
              src={page.imageURL}
              alt={page.serviceName}
              sx={styles.serviceImage}
            />
          </Box>
        )}

        {/* Description */}
        {page.description && (
          <Box component="p" sx={styles.description(theme)}>
            {page.description}
          </Box>
        )}

        {/* Two Column Layout */}
        <Box sx={styles.twoColumnLayout}>
          {/* Left Column */}
          <Box sx={styles.leftColumn}>
            {/* Active Compounds */}
            <ServiceActiveCompoundsSection
              activeCompounds={page.activeCompounds}
              label={getActiveCompoundTitle()}
            />

            {/* Attributes */}
            <ServiceAttributesSection
              attributes={page.attributes}
              label={labels.attributes}
            />

            {/* Rational */}
            <ServiceRationaleSection
              rules={rules}
              labels={{
                rational: labels.rational,
                conditions: labels.conditions,
              }}
              categoryOptions={categoryOptions}
              unitType={unitType}
            />
          </Box>

          {/* Right Column */}
          <Box sx={styles.rightColumn}>
            {/* PNOE Benefits */}
            <ServiceBenefitsSection
              benefits={page.pnoeBenefits}
              label={labels.pnoeBenefits}
              checkIconSrc={checkIconBlue}
              backgroundColor={theme.colorTokens.P[10]}
            />

            {/* Additional Benefits */}
            <ServiceBenefitsSection
              benefits={page.additionalBenefits}
              label={labels.additionalBenefits}
              checkIconSrc={checkIconGreen}
            />
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default ServicePage;

const styles = {
  pageContent: {
    padding: "20px 0 20px 0",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "18px",
  },
  serviceHeader: {
    display: "flex",
    gap: "60px",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "0 18px",
  },
  prescriptionPeriod: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    alignItems: "flex-end",
  },
  periodLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "9px",
    lineHeight: "14px",
    color: theme.colorTokens.N[80], // #6D7075
    textAlign: "right" as "right",
    margin: 0,
  }),
  periodValue: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30], // #F7F9FA
    borderRadius: "8px",
    padding: "4px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  }),
  periodText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "9px",
    lineHeight: "14px",
    color: theme.colorTokens.N[90], // #3B3F42
    textAlign: "right" as "right",
    margin: 0,
    whiteSpace: "nowrap",
  }),
  calendarIcon: {
    width: "11px",
    height: "11px",
    objectFit: "contain" as "contain",
  },
  imageContainer: {
    height: "155px",
    width: "100%",
    overflow: "hidden",
  },
  serviceImage: {
    width: "100%",
    height: "auto",
  },
  description: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    fontSize: "9px",
    lineHeight: "14px",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
    padding: "0 18px",
  }),
  twoColumnLayout: {
    display: "flex",
    gap: "18px",
    padding: "0 18px",
  },
  leftColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "18px",
  },
  rightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "18px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "normal",
    color: theme.colorTokens.N[90], // #3B3F42
    margin: 0,
  }),
};
